/**
 * Validators for condition dimensions whose value field is `string[]` or
 * `{ code: string }[]`, plus a required predicate.
 * For Media Devoted | Created By Ciro Autiero
 */

import { PREDICATE_VALUES } from '../enums';
import {
  describe,
  isPlainObject,
  isStringArray,
  joinPath,
  pushError,
  requireString,
  type ValidationCtx,
} from '../primitives';

function validateCodeList(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
): void {
  if (!Array.isArray(v)) {
    pushError(ctx, basePath, 'wrong_type', 'Expected array of code refs', {
      expected: 'array',
      received: describe(v),
    });
    return;
  }
  v.forEach((entry, idx) => {
    const path = joinPath(basePath, idx);
    if (!isPlainObject(entry)) {
      pushError(ctx, path, 'invalid_object', 'Code ref must be an object');
      return;
    }
    requireString(ctx, entry, 'code', path);
  });
}

function validateStringList(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
): void {
  if (!isStringArray(v)) {
    pushError(ctx, basePath, 'invalid_array', 'Expected string[]', {
      expected: 'string[]',
      received: describe(v),
    });
  }
}

export function validateNamesPredicateCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.names === undefined) {
    pushError(
      ctx,
      joinPath(basePath, 'names'),
      'missing_required',
      'Missing required field "names"',
    );
  } else {
    validateStringList(ctx, raw.names, joinPath(basePath, 'names'));
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateCountryCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.countries === undefined) {
    pushError(
      ctx,
      joinPath(basePath, 'countries'),
      'missing_required',
      'Missing required field "countries"',
    );
  } else {
    validateCodeList(ctx, raw.countries, joinPath(basePath, 'countries'));
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateLanguageCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  if (raw.languages === undefined) {
    pushError(
      ctx,
      joinPath(basePath, 'languages'),
      'missing_required',
      'Missing required field "languages"',
    );
  } else {
    validateCodeList(ctx, raw.languages, joinPath(basePath, 'languages'));
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}
